import { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  Users,
  Settings,
  User,
  PanelLeftClose,
  PanelLeftOpen,
  Clock,
  ShoppingBag,
  MessageSquare,
  Bell,
  BarChart3,
  Globe,
  Search,
} from 'lucide-react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { UserMenu } from './UserMenu';
import { AssistantFeedSheet } from './assistant-feed';
import { CommandPalette } from './search/CommandPalette';
import { CommandActionPalette } from './command-palette/CommandActionPalette';
import { useKeyboardShortcut } from './useKeyboardShortcut';
import { NewClientModal } from '@/pages/admin/NewClientModal';
import { useBusinessStore, usePreferencesStore } from '@/stores';
import { useQuery } from '@tanstack/react-query';
import { adminMessagesApi, adminAlertsApi } from '@/api';
import { colors, fonts, spacing, radius } from '@/theme/tokens';
import { can, type Capability } from '@tilly/shared';
import { modShortcutLabel } from '@/lib/platform';
import { AccountLinkedModal } from './AccountLinkedModal';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const SIDEBAR_EXPANDED_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;
const TOPBAR_HEIGHT = 56;
const TOPBAR_HEIGHT_MOBILE = 48;
const STORAGE_KEY = 'tilly-sidebar-collapsed';

// ---------------------------------------------------------------------------
// Nav item definition
// ---------------------------------------------------------------------------
interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  requiredCapability?: Capability;
  badge?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// AppShell — layout route component (renders <Outlet /> as content)
// ---------------------------------------------------------------------------
export function AppShell() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  // On mobile the drawer starts closed; on desktop it respects the stored preference.
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [newClientOpen, setNewClientOpen] = useState(false);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const openCommandPalette = useCallback(() => setCommandOpen(true), []);
  useKeyboardShortcut('k', openSearch);
  useKeyboardShortcut('p', openCommandPalette);

  const navigate = useNavigate();
  const activeRole = useBusinessStore((s) => s.activeRole);
  const { activeBusinessId: businessId } = useBusinessStore();
  const setLastVisitedRoute = usePreferencesStore((s) => s.setLastVisitedRoute);
  const location = useLocation();

  // Direct shortcut (bypasses any palette) — only fires outside text inputs
  // so typing "n" in a form field never accidentally triggers it.
  const openNewAppointment = useCallback(() => {
    if (!activeRole || !can(activeRole, 'appointments.manage')) return;
    navigate('/admin/calendar?newAppointment=1');
  }, [activeRole, navigate]);
  useKeyboardShortcut('n', openNewAppointment, { allowInInputs: false });

  // Track last visited admin route for "Continue where you left off"
  useEffect(() => {
    if (location.pathname.startsWith('/admin/') && location.pathname !== '/admin/dashboard') {
      setLastVisitedRoute(location.pathname);
    }
  }, [location.pathname, setLastVisitedRoute]);

  // Poll for unread message count
  const unreadQuery = useQuery({
    queryKey: ['unread-count', businessId],
    queryFn: () => adminMessagesApi.getUnreadCount(businessId!),
    enabled: !!businessId && (activeRole === 'owner' || activeRole === 'admin'),
    refetchInterval: 30_000,
  });
  const unreadCount = unreadQuery.data?.count ?? 0;

  // On mobile, poll alert count for the bell badge in the topbar.
  // On desktop, AssistantFeed on the dashboard owns these queries.
  const alertCountQuery = useQuery({
    queryKey: ['business-alerts', businessId],
    queryFn: () => adminAlertsApi.getAlerts(businessId!),
    enabled: isMobile && !!businessId,
    refetchInterval: 60_000,
    select: (data) => data.alerts.length,
  });
  const unmatchedCountQuery = useQuery({
    queryKey: ['unmatched-messages', businessId],
    queryFn: () => adminMessagesApi.getUnmatched(businessId!),
    enabled: isMobile && !!businessId,
    refetchInterval: 60_000,
    select: (data) => data.total,
  });
  // Total: business alerts + unread conversations + unmatched inbound
  const alertCount = (alertCountQuery.data ?? 0) + unreadCount + (unmatchedCountQuery.data ?? 0);

  const NAV_ITEMS: NavItem[] = [
    {
      path: '/admin/dashboard',
      label: t('nav.dashboard'),
      icon: <LayoutDashboard size={20} />,
    },
    {
      path: '/admin/calendar',
      label: t('nav.calendar'),
      icon: <Calendar size={20} />,
      requiredCapability: 'appointments.view',
    },
    {
      path: '/admin/schedule',
      label: t('nav.mySchedule'),
      icon: <Clock size={20} />,
      requiredCapability: 'schedule.self.manage',
    },
    {
      path: '/admin/clients',
      label: t('nav.clients'),
      icon: <Users size={20} />,
      requiredCapability: 'clients.view',
    },
    {
      path: '/admin/messages',
      label: t('nav.messages'),
      icon: <MessageSquare size={20} />,
      requiredCapability: 'messages.view',
      badge:
        unreadCount > 0 ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              borderRadius: '9px',
              backgroundColor: colors.accent,
              color: '#fff',
              fontSize: '11px',
              fontWeight: 700,
              padding: '0 4px',
              lineHeight: 1,
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : undefined,
    },
    {
      path: '/admin/providers',
      label: t('nav.providers'),
      icon: <Users size={20} />,
      requiredCapability: 'team.manage',
    },
    {
      path: '/admin/settings',
      label: t('nav.settings'),
      icon: <Settings size={20} />,
      requiredCapability: 'business.settings.manage',
    },
    {
      path: '/admin/catalog',
      label: t('nav.catalog'),
      icon: <ShoppingBag size={20} />,
      requiredCapability: 'services.manage',
    },
    {
      path: '/admin/booking-page',
      label: t('nav.bookingPage'),
      icon: <Globe size={20} />,
      requiredCapability: 'business.settings.manage',
    },
    {
      path: '/admin/reports',
      label: t('nav.reports'),
      icon: <BarChart3 size={20} />,
      requiredCapability: 'reports.view',
    },
    {
      path: '/admin/profile',
      label: t('nav.profile'),
      icon: <User size={20} />,
    },
  ];

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  const handleToggle = () => {
    if (isMobile) {
      setMobileDrawerOpen((prev) => !prev);
    } else {
      setCollapsed((prev) => {
        const next = !prev;
        try {
          localStorage.setItem(STORAGE_KEY, String(next));
        } catch {
          // ignore
        }
        return next;
      });
    }
  };

  // Filter nav items to those visible for this role
  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.requiredCapability || (activeRole && can(activeRole, item.requiredCapability))
  );

  const topbarHeight = isMobile ? TOPBAR_HEIGHT_MOBILE : TOPBAR_HEIGHT;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: colors.bg,
      }}
    >
      <AccountLinkedModal />
      {/* ── Top Bar ── */}
      <TopBar
        collapsed={collapsed}
        onToggle={handleToggle}
        isMobile={isMobile}
        alertCount={alertCount}
        onAlertClick={() => setSheetOpen(true)}
        onSearchClick={openSearch}
        topbarHeight={topbarHeight}
      />

      {businessId && (
        <>
          <CommandPalette
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
            businessId={businessId}
          />
          <CommandActionPalette
            open={commandOpen}
            onClose={() => setCommandOpen(false)}
            onNewClient={() => {
              setCommandOpen(false);
              setNewClientOpen(true);
            }}
          />
          <NewClientModal
            businessId={businessId}
            open={newClientOpen}
            onClose={() => setNewClientOpen(false)}
          />
        </>
      )}

      {/* ── Body ── */}
      <div style={{ display: 'flex', flex: 1, paddingTop: topbarHeight }}>
        {isMobile ? (
          <>
            {/* Backdrop */}
            {mobileDrawerOpen && (
              <div
                onClick={() => setMobileDrawerOpen(false)}
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 150,
                  backgroundColor: 'rgba(0,0,0,0.35)',
                }}
              />
            )}
            {/* Slide-in drawer */}
            <nav
              style={{
                position: 'fixed',
                top: topbarHeight,
                left: 0,
                bottom: 0,
                width: SIDEBAR_EXPANDED_WIDTH,
                backgroundColor: colors.surface,
                borderRight: `1px solid ${colors.border}`,
                display: 'flex',
                flexDirection: 'column',
                zIndex: 160,
                transform: mobileDrawerOpen
                  ? 'translateX(0)'
                  : `translateX(-${SIDEBAR_EXPANDED_WIDTH}px)`,
                transition: 'transform 240ms ease',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  flex: 1,
                  paddingTop: spacing[2],
                  paddingBottom: spacing[2],
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }}
              >
                {visibleItems.map((item) => (
                  <SidebarNavItem
                    key={item.path}
                    item={item}
                    collapsed={false}
                    compact
                    onNavigate={() => setMobileDrawerOpen(false)}
                  />
                ))}
              </div>
            </nav>

            {/* Content — full width on mobile */}
            <main style={{ flex: 1, minWidth: 0, backgroundColor: colors.bg }}>
              <Outlet />
            </main>
          </>
        ) : (
          <>
            {/* Desktop: persistent sidebar */}
            <Sidebar collapsed={collapsed} items={visibleItems} width={sidebarWidth} />

            {/* Content */}
            <main
              style={{
                flex: 1,
                minWidth: 0,
                marginLeft: sidebarWidth,
                transition: 'margin-left 250ms ease',
                backgroundColor: colors.bg,
              }}
            >
              <Outlet />
            </main>
          </>
        )}
      </div>

      {/* Mobile assistant feed bottom sheet */}
      <AssistantFeedSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top Bar
// ---------------------------------------------------------------------------
function TopBar({
  collapsed,
  onToggle,
  isMobile,
  alertCount,
  onAlertClick,
  onSearchClick,
  topbarHeight,
}: {
  collapsed: boolean;
  onToggle: () => void;
  isMobile: boolean;
  alertCount: number;
  onAlertClick: () => void;
  onSearchClick: () => void;
  topbarHeight: number;
}) {
  const { t } = useTranslation();
  const translate = (key: string, options?: Record<string, unknown>) =>
    t(key as never, options as never) as unknown as string;
  return (
    <header
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: topbarHeight,
        backgroundColor: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: isMobile ? spacing[3] : spacing[4],
        paddingRight: isMobile ? spacing[3] : spacing[4],
        zIndex: 200,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      {/* Left: toggle + wordmark */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: isMobile ? spacing[2] : spacing[3] }}
      >
        <button
          onClick={onToggle}
          aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            background: 'none',
            border: 'none',
            borderRadius: radius.md,
            color: colors.textMuted,
            cursor: 'pointer',
            transition: 'background-color 150ms ease, color 150ms ease',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.accentLighter;
            (e.currentTarget as HTMLButtonElement).style.color = colors.accent;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = colors.textMuted;
          }}
        >
          {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
        </button>

        {/* Wordmark */}
        <NavLink to="/admin/dashboard" style={{ textDecoration: 'none' }}>
          <span
            style={{
              fontFamily: fonts.heading,
              fontSize: isMobile ? '18px' : '22px',
              fontWeight: 700,
              color: colors.accent,
              letterSpacing: '-0.01em',
              lineHeight: 1,
            }}
          >
            tilly
          </span>
        </NavLink>
      </div>

      {/* Right: search + bell badge (mobile only) + user menu */}
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
        <button
          onClick={onSearchClick}
          // eslint-disable-next-line i18next/no-literal-string -- already a t() call with an i18n key
          aria-label={translate('admin:search.trigger')}
          title={
            isMobile
              ? undefined
              : // eslint-disable-next-line i18next/no-literal-string -- already a t() call with an i18n key
                translate('admin:search.shortcutHint', { key: modShortcutLabel('K') })
          }
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing[2],
            width: isMobile ? 36 : undefined,
            height: 36,
            padding: isMobile ? 0 : `0 ${spacing[3]}`,
            background: 'none',
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            color: colors.textMuted,
            cursor: 'pointer',
            transition: 'background-color 150ms ease, color 150ms ease',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.accentLighter;
            (e.currentTarget as HTMLButtonElement).style.color = colors.accent;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = colors.textMuted;
          }}
        >
          <Search size={18} />
          {!isMobile && <span style={{ fontSize: '13px' }}>{modShortcutLabel('K')}</span>}
        </button>
        {isMobile && (
          <button
            onClick={onAlertClick}
            // eslint-disable-next-line i18next/no-literal-string -- already a t() call with an i18n key
            aria-label={translate('admin:assistantFeed.alerts_other', { count: alertCount })}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              background: 'none',
              border: 'none',
              borderRadius: radius.md,
              color: colors.textMuted,
              cursor: 'pointer',
              transition: 'background-color 150ms ease, color 150ms ease',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.accentLighter;
              (e.currentTarget as HTMLButtonElement).style.color = colors.accent;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
              (e.currentTarget as HTMLButtonElement).style.color = colors.textMuted;
            }}
          >
            <Bell size={20} />
            {alertCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 16,
                  height: 16,
                  borderRadius: '8px',
                  backgroundColor: colors.brass,
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: 700,
                  padding: '0 3px',
                  lineHeight: 1,
                }}
              >
                {alertCount > 99 ? '99+' : alertCount}
              </span>
            )}
          </button>
        )}
        <UserMenu />
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------
function Sidebar({
  collapsed,
  items,
  width,
}: {
  collapsed: boolean;
  items: NavItem[];
  width: number;
}) {
  const { t } = useTranslation();
  return (
    <nav
      style={{
        position: 'fixed',
        top: TOPBAR_HEIGHT,
        left: 0,
        bottom: 0,
        width,
        backgroundColor: colors.surface,
        borderRight: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 250ms ease',
        overflow: 'hidden',
        zIndex: 100,
      }}
    >
      {/* Nav items */}
      <div
        style={{
          flex: 1,
          paddingTop: spacing[3],
          paddingBottom: spacing[3],
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {items.map((item) => (
          <SidebarNavItem key={item.path} item={item} collapsed={collapsed} />
        ))}
      </div>

      {/* Footer: collapse toggle hint */}
      {!collapsed && (
        <div
          style={{
            padding: `${spacing[4]} ${spacing[4]}`,
            borderTop: `1px solid ${colors.border}`,
          }}
        >
          <p
            style={{
              fontSize: '11px',
              color: colors.textMuted,
              margin: 0,
              whiteSpace: 'nowrap',
              opacity: 0.7,
            }}
          >
            {t('sidebar.footer')}
          </p>
        </div>
      )}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Sidebar nav item
// ---------------------------------------------------------------------------
function SidebarNavItem({
  item,
  collapsed,
  compact = false,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  compact?: boolean;
  onNavigate?: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <NavLink
      to={item.path}
      end={item.path === '/admin/dashboard'}
      data-testid={`nav-${item.label.toLowerCase()}`}
      title={collapsed ? item.label : undefined}
      onClick={onNavigate}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: compact ? spacing[2] : spacing[3],
        padding: compact ? `8px ${spacing[3]}` : `10px ${spacing[4]}`,
        marginLeft: spacing[2],
        marginRight: spacing[2],
        marginBottom: '2px',
        borderRadius: radius.md,
        textDecoration: 'none',
        position: 'relative',
        transition: 'background-color 150ms ease',
        whiteSpace: 'nowrap',
        backgroundColor: isActive
          ? colors.accentLighter
          : hovered
            ? colors.surfaceRaised
            : 'transparent',
        color: isActive ? colors.accent : colors.textSecondary,
        fontWeight: isActive ? 600 : 400,
      })}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              style={{
                position: 'absolute',
                left: 0,
                top: '20%',
                bottom: '20%',
                width: 3,
                borderRadius: radius.full,
                backgroundColor: colors.accent,
              }}
            />
          )}

          {/* Icon */}
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: 20,
              marginLeft: 2,
            }}
          >
            {item.icon}
          </span>

          {/* Label — hidden when collapsed */}
          {!collapsed && (
            <span
              style={{
                fontSize: compact ? '13px' : '14px',
                overflow: 'hidden',
                opacity: 1,
                transition: 'opacity 200ms ease',
                fontFamily: fonts.body,
                flex: 1,
              }}
            >
              {item.label}
            </span>
          )}

          {/* Badge */}
          {!collapsed && item.badge}
        </>
      )}
    </NavLink>
  );
}
